import { useState, useRef, useMemo } from 'react'
import { useDepartment } from '../../context/DepartmentContext'
import { useToast } from '../../context/ToastContext'
import { getProducts, fuzzyMatch } from '../../data/products'
import { getProductPrice } from '../../lib/productPricing'
import { getCategories } from '../../data/categories'
import { BAKERY_CATEGORIES_SEED } from '../../data/bakeryData'
import { YAWEHS_CATEGORIES_SEED } from '../../data/yawehsData'
import {
  saveSalesBatch, saveFlagsBatch, saveMorningLog, saveSurplusEntry,
  saveProductionBatch, saveDistributionBatch, saveDamagesBatch, saveBakerySurplusBatch,
  saveExpenditureBatch, saveCashOnHand, deleteSalesForDate,
} from '../../lib/store'
import { generateTransactionFlags } from '../../lib/flagging'
import { Upload, FileCheck, Check, AlertCircle, CalendarDays, ChevronDown, Trash2, Info } from 'lucide-react'
import AddProductModal from './AddProductModal'
import DateInput from '../ui/DateInput'

// Format a Date object as YYYY-MM-DD using LOCAL timezone (not UTC)
function toLocalDateStr(d) {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function parseDate(val) {
  if (!val) return null
  // Handle JavaScript Date objects (produced by xlsx with cellDates: true)
  if (val instanceof Date) {
    if (isNaN(val.getTime())) return null
    return toLocalDateStr(val)
  }
  if (typeof val === 'number') {
    const d = new Date((val - 25569) * 86400 * 1000)
    if (isNaN(d.getTime())) return null
    return toLocalDateStr(d)
  }
  const str = String(val).trim()
  // yyyy-mm-dd
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str
  // dd/mm/yyyy (South African format)
  const dmy = str.match(/^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{4})$/)
  if (dmy) return `${dmy[3]}-${dmy[2].padStart(2, '0')}-${dmy[1].padStart(2, '0')}`
  // dd/mm/yy (2-digit year)
  const dmy2 = str.match(/^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{2})$/)
  if (dmy2) {
    const yr = Number(dmy2[3])
    const fullYear = yr >= 50 ? 1900 + yr : 2000 + yr
    return `${fullYear}-${dmy2[2].padStart(2, '0')}-${dmy2[1].padStart(2, '0')}`
  }
  // "5 Jan 2026", "05-Jan-2026", etc.
  const namedMonth = str.match(/^(\d{1,2})[\s\-.]([A-Za-z]{3,9})[\s\-.,](\d{2,4})$/)
  if (namedMonth) {
    const d = new Date(`${namedMonth[2]} ${namedMonth[1]}, ${namedMonth[3]}`)
    if (!isNaN(d.getTime())) return toLocalDateStr(d)
  }
  // "Jan 5, 2026" / "January 5 2026"
  const namedMonth2 = str.match(/^([A-Za-z]{3,9})[\s\-.](\d{1,2})[,\s]+(\d{2,4})$/)
  if (namedMonth2) {
    const d = new Date(`${namedMonth2[1]} ${namedMonth2[2]}, ${namedMonth2[3]}`)
    if (!isNaN(d.getTime())) return toLocalDateStr(d)
  }
  // Last resort: try native Date parsing (handles Date.toString() output)
  const fallback = new Date(str)
  if (!isNaN(fallback.getTime()) && fallback.getFullYear() >= 2000 && fallback.getFullYear() <= 2100) {
    return toLocalDateStr(fallback)
  }
  return null
}

function detectSheetType(sheetName, headers, department, fallback = 'sales') {
  const name = (sheetName || '').toLowerCase()
  const h = (headers || []).map(h => String(h).toLowerCase())
  // Yoco POS transaction format: has "receipt" and "items" columns
  if (h.includes('receipt') && h.includes('items')) return 'yoco'
  if (name.includes('transaction')) return 'yoco'
  if (department === 'bakery' || department === 'yawehs') {
    if (name.includes('production') || name.includes('baking')) return 'production'
    if (name.includes('distribution') || name.includes('dist')) return 'distribution'
    if (name.includes('sandwich')) return 'distribution'
    if (name.includes('surplus') || name.includes('closing')) return 'surplus'
    if (name.includes('damage')) return 'damages'
    if (name.includes('expenditure') || name.includes('expense')) return 'expenditure'
    if (name.includes('cash')) return 'cash'
  }
  if (name.includes('morning') || name.includes('opening')) return 'morning'
  if (name.includes('surplus') || name.includes('end') || name.includes('closing')) return 'surplus'
  if (name.includes('rman') || name.includes('sales') || name.includes('pos')) return 'sales'
  if (h.some(x => x.includes('opening') || x.includes('morning'))) return 'morning'
  if (h.some(x => x.includes('surplus') || x.includes('closing'))) return 'surplus'
  if (h.some(x => x.includes('production') || x.includes('produced'))) return 'production'
  if (h.some(x => x.includes('distribution') || x.includes('sent'))) return 'distribution'
  if (h.some(x => x.includes('damage'))) return 'damages'
  return fallback
}

function findHeaderRow(json) {
  const keywords = ['date', 'product', 'item', 'category', 'qty', 'quantity', 'sales', 'amount', 'opening', 'surplus', 'morning', 'produced', 'sent', 'sold', 'damage', 'name', 'code', 'total', 'price', 'unit']
  // Skip completely blank leading rows
  let startIdx = 0
  for (let i = 0; i < Math.min(json.length, 4); i++) {
    const row = (json[i] || []).filter(c => c !== '' && c !== null && c !== undefined)
    if (row.length === 0) { startIdx = i + 1; continue }
    break
  }
  // First pass: 2+ keyword matches (strong header signal)
  for (let i = startIdx; i < Math.min(json.length, startIdx + 6); i++) {
    const row = (json[i] || []).map(c => String(c).toLowerCase().trim())
    const matchCount = row.filter(c => keywords.some(k => c.includes(k))).length
    if (matchCount >= 2) return i
  }
  // Second pass: 1+ keyword match BUT the row must be mostly text (not data)
  for (let i = startIdx; i < Math.min(json.length, startIdx + 6); i++) {
    const row = (json[i] || []).filter(c => c !== '' && c !== null && c !== undefined)
    if (row.length === 0) continue
    const lowerRow = row.map(c => String(c).toLowerCase().trim())
    const matchCount = lowerRow.filter(c => keywords.some(k => c.includes(k))).length
    const textCells = row.filter(c => isNaN(Number(c)) && !parseDate(c))
    // Must have keyword match AND be mostly text (not a data row that happens to contain "total")
    if (matchCount >= 1 && textCells.length > row.length / 2) return i
  }
  // Fallback: first non-blank row that is mostly text (not numbers/dates)
  for (let i = startIdx; i < Math.min(json.length, startIdx + 4); i++) {
    const row = (json[i] || []).filter(c => c !== '' && c !== null && c !== undefined)
    if (row.length === 0) continue
    const textCells = row.filter(c => isNaN(Number(c)) && !parseDate(c))
    if (textCells.length > row.length / 2) return i
  }
  // No header detected — return -1 to signal "no header row"
  return -1
}

function formatCellForDisplay(val) {
  if (val instanceof Date && !isNaN(val.getTime())) {
    return val.toLocaleDateString('en-ZA')
  }
  return String(val ?? '')
}

function getRowDateRange(rows, dateColIdx = 0) {
  const dates = new Set()
  for (const row of rows) {
    const d = parseDate(row[dateColIdx])
    if (d) dates.add(d)
  }
  const sorted = [...dates].sort()
  return { dates: sorted, min: sorted[0] || null, max: sorted[sorted.length - 1] || null, count: sorted.length }
}

export default function ExcelUpload({ dataType, dateRange, onSaved }) {
  const { currentDepartmentId } = useDepartment()
  const { addToast } = useToast()
  const fileRef = useRef()
  const [parsed, setParsed] = useState(null)
  const [loading, setLoading] = useState(false)
  const [saved, setSaved] = useState(false)
  const [showAddProduct, setShowAddProduct] = useState(false)
  const [addProductForUnmatched, setAddProductForUnmatched] = useState(null)
  const [overrideDate, setOverrideDate] = useState('')
  const [importSummary, setImportSummary] = useState(null)

  const department = currentDepartmentId
  const isBakery = department === 'bakery' || department === 'yawehs'

  const sheetTypeOptions = isBakery
    ? [
        { value: 'production', label: 'Production Log' },
        { value: 'distribution', label: 'Distribution' },
        { value: 'surplus', label: 'Surplus' },
        { value: 'damages', label: 'Damages' },
        { value: 'sales', label: 'Sales' },
        { value: 'yoco', label: 'Yoco Transactions' },
        ...(department === 'yawehs' ? [
          { value: 'expenditure', label: 'Expenditure' },
          { value: 'cash', label: 'Cash on Hand' },
        ] : []),
      ]
    : [
        { value: 'morning', label: 'Morning Log' },
        { value: 'surplus', label: 'Surplus' },
        { value: 'sales', label: 'Sales' },
        { value: 'yoco', label: 'Yoco Transactions' },
      ]

  const handleFile = async (e) => {
    const file = e.target.files[0]
    if (!file) return
    setLoading(true)
    setSaved(false)
    setImportSummary(null)

    try {
      const XLSX = await import('xlsx')
      const buffer = await file.arrayBuffer()
      const workbook = XLSX.read(buffer, { type: 'array', cellDates: true })

      const sheets = []
      const sheetCount = workbook.SheetNames.length

      for (const sheetName of workbook.SheetNames) {
        const ws = workbook.Sheets[sheetName]
        const json = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '', raw: true })
        if (json.length < 2) continue

        const headerIdx = findHeaderRow(json)
        const hasHeader = headerIdx >= 0
        const headers = hasHeader ? (json[headerIdx] || []) : json[0]?.map((_, i) => `Col ${i + 1}`) || []

        // Try to parse the sheet name as a date (e.g. "02 Jan 2026", "14 Mar 2026")
        const sheetDate = parseDate(sheetName)

        // For type detection: always check for Yoco format first (even single-sheet files),
        // then fall back to dataType for single sheets or date-named sheets.
        const detected = detectSheetType(sheetName, hasHeader ? headers : [], department, dataType)
        const type = detected === 'yoco'
          ? 'yoco'  // Yoco format always wins
          : sheetCount === 1
            ? dataType
            : sheetDate
              ? dataType  // date-named sheets: use selected data type
              : detected

        const dataRows = json.slice(hasHeader ? headerIdx + 1 : 0).filter(row => row.some(c => c !== '' && c !== null && c !== undefined))

        // Detect if column 0 contains dates (or if all data is non-date because sheet name IS the date)
        let hasDateColumn = true
        if (sheetDate) {
          const sample = dataRows.slice(0, Math.min(5, dataRows.length))
          const nonEmpty = sample.filter(r => r[0] !== '' && r[0] !== null && r[0] !== undefined)
          if (nonEmpty.length > 0) {
            const dateHits = nonEmpty.filter(r => parseDate(r[0])).length
            hasDateColumn = dateHits >= Math.ceil(nonEmpty.length * 0.5)
          } else {
            hasDateColumn = false
          }
        }

        const rowDates = getRowDateRange(dataRows, hasDateColumn ? 0 : -1)

        sheets.push({
          sheetName,
          type,
          headers,
          headerIdx,
          rows: dataRows,
          sourceFile: file.name,
          rowDateRange: rowDates,
          sheetDate,        // date parsed from sheet name (null if not a date)
          hasDateColumn,    // whether column 0 in data rows contains dates
        })
      }

      setParsed(sheets)
    } catch (err) {
      addToast('Failed to parse Excel: ' + err.message, 'error')
    } finally {
      setLoading(false)
    }
  }

  // Reactively compute unmatched based on current parsed state (updates when sheet type changes)
  const unmatched = useMemo(() => {
    if (!parsed) return []
    const unmatchedRows = []
    const categories = department === 'yawehs' ? YAWEHS_CATEGORIES_SEED : isBakery ? BAKERY_CATEGORIES_SEED : getCategories()

    for (const sheet of parsed) {
      const noDateCol = sheet.sheetDate && !sheet.hasDateColumn
      const prodCol = noDateCol ? 0 : 1

      if (sheet.type === 'sales' || sheet.type === 'distribution') {
        for (const row of sheet.rows) {
          const nameOrCode = String(row[prodCol] || '').trim()
          if (!nameOrCode) continue
          const match = fuzzyMatch(nameOrCode, department)
          if (!match) {
            unmatchedRows.push({ rawName: nameOrCode, sheetName: sheet.sheetName })
          }
        }
      }

      if (sheet.type === 'morning' || (sheet.type === 'surplus' && !isBakery)) {
        const startCol = noDateCol ? 0 : 1
        for (let i = startCol; i < sheet.headers.length; i++) {
          const headerName = String(sheet.headers[i] || '').toLowerCase().trim()
          if (!headerName || headerName.startsWith('col ')) continue
          const cat = categories.find(c =>
            c.name.toLowerCase() === headerName ||
            c.label?.toLowerCase() === headerName
          ) || categories.find(c =>
            c.name.toLowerCase().includes(headerName) ||
            headerName.includes(c.name.toLowerCase())
          )
          if (!cat) {
            unmatchedRows.push({ rawName: String(sheet.headers[i]).trim(), sheetName: sheet.sheetName })
          }
        }
      }
    }

    const seen = new Set()
    return unmatchedRows.filter(u => {
      const key = u.rawName.toLowerCase()
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
  }, [parsed, department, isBakery])

  const updateSheetType = (idx, newType) => {
    setParsed(prev => {
      const sheets = [...prev]
      sheets[idx] = { ...sheets[idx], type: newType }
      return sheets
    })
  }

  const updateCell = (sheetIdx, rowIdx, colIdx, value) => {
    setParsed(prev => {
      const sheets = [...prev]
      const rows = [...sheets[sheetIdx].rows]
      const row = [...rows[rowIdx]]
      row[colIdx] = value
      rows[rowIdx] = row
      sheets[sheetIdx] = { ...sheets[sheetIdx], rows }
      return sheets
    })
  }

  const removeRow = (sheetIdx, rowIdx) => {
    setParsed(prev => {
      const sheets = [...prev]
      sheets[sheetIdx] = { ...sheets[sheetIdx], rows: sheets[sheetIdx].rows.filter((_, i) => i !== rowIdx) }
      return sheets
    })
  }

  const saveAll = async () => {
    if (!parsed) return
    let totalSaved = 0
    let totalFlags = 0
    const datesProcessed = new Set()
    const importedSheets = []
    const skippedSheets = []

    const categories = department === 'yawehs' ? YAWEHS_CATEGORIES_SEED : isBakery ? BAKERY_CATEGORIES_SEED : getCategories()

    // Determine date range bounds for filtering (if dateRange prop is provided)
    let rangeMin = null, rangeMax = null
    if (dateRange && dateRange.length > 0) {
      rangeMin = dateRange[0]
      rangeMax = dateRange[dateRange.length - 1]
    }

    for (const sheet of parsed) {
      // If sheet has a date from its name, validate against dateRange
      if (sheet.sheetDate && rangeMin && rangeMax) {
        if (sheet.sheetDate < rangeMin || sheet.sheetDate > rangeMax) {
          skippedSheets.push({ name: sheet.sheetName, reason: `Outside date range (${rangeMin} to ${rangeMax})` })
          continue
        }
      }

      const noDateCol = sheet.sheetDate && !sheet.hasDateColumn
      let sheetSaved = 0

      // Helper to resolve the date for a given row
      const resolveDate = (row) => {
        if (sheet.sheetDate) return sheet.sheetDate
        return overrideDate || parseDate(row[0])
      }

      if (sheet.type === 'morning' || (sheet.type === 'surplus' && !isBakery)) {
        const startCol = noDateCol ? 0 : 1
        for (const row of sheet.rows) {
          const date = resolveDate(row)
          if (!date) continue

          // Assertion: if sheetDate exists, date MUST match
          if (sheet.sheetDate && date !== sheet.sheetDate) {
            console.error(`Date assertion failed: sheet "${sheet.sheetName}" expects ${sheet.sheetDate} but got ${date}`)
            continue
          }

          datesProcessed.add(date)

          for (let i = startCol; i < row.length; i++) {
            const headerName = String(sheet.headers[i] || '').toLowerCase().trim()
            if (!headerName || headerName.startsWith('col ')) continue
            const cat = categories.find(c =>
              c.name.toLowerCase() === headerName ||
              c.label?.toLowerCase() === headerName
            ) || categories.find(c =>
              c.name.toLowerCase().includes(headerName) ||
              headerName.includes(c.name.toLowerCase())
            )
            if (cat && row[i] !== '' && row[i] !== null) {
              if (sheet.type === 'morning') {
                saveMorningLog(department, date, cat.name, Number(row[i]), cat.openingUnit, 'excel')
              } else {
                saveSurplusEntry(department, date, cat.name, Number(row[i]), cat.surplusUnit, 'excel')
              }
              sheetSaved++
              totalSaved++
            }
          }
        }
      } else if (sheet.type === 'production' && isBakery) {
        const prodCol = noDateCol ? 0 : 1
        const qtyCol = noDateCol ? 1 : 2
        for (const row of sheet.rows) {
          const date = resolveDate(row)
          if (!date) continue
          if (sheet.sheetDate && date !== sheet.sheetDate) continue
          datesProcessed.add(date)
          const nameOrCode = String(row[prodCol] || '').trim()
          const qty = parseFloat(row[qtyCol])
          if (!nameOrCode || isNaN(qty)) continue
          const match = fuzzyMatch(nameOrCode, department)
          if (!match) continue
          saveProductionBatch(date, 'day', [{ productCode: match.product.code, qtyProduced: qty }], department)
          sheetSaved++
          totalSaved++
        }
      } else if (sheet.type === 'damages' && isBakery) {
        const prodCol = noDateCol ? 0 : 1
        const qtyCol = noDateCol ? 1 : 2
        const byDate = {}
        for (const row of sheet.rows) {
          const date = resolveDate(row)
          if (!date) continue
          if (sheet.sheetDate && date !== sheet.sheetDate) continue
          datesProcessed.add(date)
          const nameOrCode = String(row[prodCol] || '').trim()
          const qty = parseFloat(row[qtyCol])
          if (!nameOrCode || isNaN(qty)) continue
          const match = fuzzyMatch(nameOrCode, department)
          if (!match) continue
          if (!byDate[date]) byDate[date] = []
          byDate[date].push({ productCode: match.product.code, qty })
          sheetSaved++
          totalSaved++
        }
        for (const [date, entries] of Object.entries(byDate)) {
          saveDamagesBatch(date, entries, department)
        }
      } else if (sheet.type === 'surplus' && isBakery) {
        const prodCol = noDateCol ? 0 : 1
        const qtyCol = noDateCol ? 1 : 2
        const byDate = {}
        for (const row of sheet.rows) {
          const date = resolveDate(row)
          if (!date) continue
          if (sheet.sheetDate && date !== sheet.sheetDate) continue
          datesProcessed.add(date)
          const nameOrCode = String(row[prodCol] || '').trim()
          const qty = parseFloat(row[qtyCol])
          if (!nameOrCode || isNaN(qty)) continue
          const match = fuzzyMatch(nameOrCode, department)
          if (!match) continue
          if (!byDate[date]) byDate[date] = []
          byDate[date].push({ productCode: match.product.code, actualSurplus: qty })
          sheetSaved++
          totalSaved++
        }
        for (const [date, entries] of Object.entries(byDate)) {
          saveBakerySurplusBatch(date, entries, department)
        }
      } else if (sheet.type === 'expenditure' && department === 'yawehs') {
        const descCol = noDateCol ? 0 : 1
        const amtCol = noDateCol ? 1 : 2
        const byDate = {}
        for (const row of sheet.rows) {
          const date = resolveDate(row)
          if (!date) continue
          if (sheet.sheetDate && date !== sheet.sheetDate) continue
          datesProcessed.add(date)
          const description = String(row[descCol] || '').trim()
          const amount = parseFloat(row[amtCol])
          if (!description || isNaN(amount)) continue
          if (!byDate[date]) byDate[date] = []
          byDate[date].push({ description, amount })
          sheetSaved++
          totalSaved++
        }
        for (const [date, entries] of Object.entries(byDate)) {
          saveExpenditureBatch(date, entries, department)
        }
      } else if (sheet.type === 'cash' && department === 'yawehs') {
        const cashCol = noDateCol ? 0 : 1
        const cardCol = noDateCol ? 1 : 2
        for (const row of sheet.rows) {
          const date = resolveDate(row)
          if (!date) continue
          if (sheet.sheetDate && date !== sheet.sheetDate) continue
          datesProcessed.add(date)
          const cash = parseFloat(row[cashCol])
          const card = parseFloat(row[cardCol])
          if (isNaN(cash) && isNaN(card)) continue
          saveCashOnHand(date, cash || 0, card || 0, department)
          sheetSaved++
          totalSaved++
        }
      } else if (sheet.type === 'yoco') {
        // Yoco POS transaction format: Receipt | Date | Time | Status | Items
        const SKIP_YOCO = /^(card payment|card charge|water|juice|cooldrink|coke|fanta|sprite|drink|creamer)/i
        const hLower = sheet.headers.map(h => String(h).toLowerCase().trim())
        const itemsIdx = hLower.indexOf('items')
        const dateIdx = hLower.indexOf('date')
        const statusIdx = hLower.indexOf('status')
        if (itemsIdx < 0) {
          skippedSheets.push({ name: sheet.sheetName, reason: 'No "Items" column found' })
          continue
        }
        const products = getProducts(department)
        const normalizeName = (s) => s.toLowerCase()
          .replace(/\bn\b/g, '&')
          .replace(/schoolbuns/g, 'school buns')
          .replace(/(\d)scb\b/g, '$1 school buns')
          .replace(/\bscb\b/g, 'school bun')
          .replace(/[^a-z0-9&\s]/g, '')
          .replace(/\s+/g, ' ')
          .trim()
        const matchProduct = (itemName) => {
          const norm = normalizeName(itemName)
          let match = products.find(p => normalizeName(p.name) === norm || p.code.toLowerCase() === norm)
          if (match) return match
          const firstWord = itemName.trim().split(/\s+/)[0]
          if (firstWord && !/^\d{5,}$/.test(firstWord)) {
            match = products.find(p => p.code.toLowerCase() === firstWord.toLowerCase())
            if (match) return match
          }
          if (norm.length >= 4) {
            match = products.find(p => {
              const pn = normalizeName(p.name)
              return pn.includes(norm) || norm.includes(pn)
            })
            if (match) return match
          }
          return null
        }
        // Group items by date
        const byDate = {}
        for (const row of sheet.rows) {
          if (statusIdx >= 0) {
            const status = String(row[statusIdx] || '').trim().toLowerCase()
            if (status && status !== 'approved') continue
          }
          const itemsStr = String(row[itemsIdx] || '').trim()
          if (!itemsStr) continue
          let rowDate = overrideDate || null
          if (dateIdx >= 0 && row[dateIdx]) {
            rowDate = parseDate(row[dateIdx])
          }
          if (!rowDate) continue
          datesProcessed.add(rowDate)
          if (!byDate[rowDate]) byDate[rowDate] = {}
          for (const part of itemsStr.split(',')) {
            const m = part.trim().match(/^(\d+)\s*x\s+(.+)$/i)
            if (!m) continue
            const qty = Number(m[1])
            const name = m[2].trim()
            if (SKIP_YOCO.test(name)) continue
            byDate[rowDate][name] = (byDate[rowDate][name] || 0) + qty
          }
        }
        // Save each date's sales as distribution entries
        for (const [d, dayItems] of Object.entries(byDate)) {
          const entries = []
          for (const [name, qty] of Object.entries(dayItems)) {
            const product = matchProduct(name)
            if (product) {
              entries.push({
                productCode: product.code,
                qtySent: 0,
                qtySold: qty,
                salesRands: qty * (getProductPrice(product, department) || 0),
                reportedSurplus: 0,
                damages: 0,
              })
            }
          }
          if (entries.length > 0) {
            saveDistributionBatch(d, 'yoco-pos', entries, department)
            sheetSaved += entries.length
            totalSaved += entries.length
          }
        }
      } else {
        // Sales (kitchen or bakery) — default handler
        const prodCol = noDateCol ? 0 : 1
        const qtyCol = noDateCol ? 1 : 2
        const salesCol = noDateCol ? 2 : 3
        const transactions = []
        for (const row of sheet.rows) {
          const date = resolveDate(row)
          const nameOrCode = String(row[prodCol] || '').trim()
          const qty = parseFloat(row[qtyCol])
          const sales = parseFloat(row[salesCol])

          if (!date || !nameOrCode || isNaN(sales)) continue

          // Assertion: if sheetDate exists, the date used MUST match
          if (sheet.sheetDate && date !== sheet.sheetDate) {
            console.error(`Date assertion failed: sheet "${sheet.sheetName}" expects ${sheet.sheetDate} but got ${date}`)
            continue
          }

          datesProcessed.add(date)

          const match = fuzzyMatch(nameOrCode, department)
          if (!match) continue

          transactions.push({
            date,
            productCode: match.product.code,
            productName: match.product.name,
            category: match.product.category,
            qtySold: isNaN(qty) ? 0 : qty,
            salesRands: sales,
            unitPrice: getProductPrice(match.product, department) ?? match.product.unitPrice,
            qps: match.product.qps,
            saleType: match.product.saleType,
            entryMode: 'excel',
            sourceFile: sheet.sourceFile,
          })
        }

        // Delete existing sales for this date before saving to prevent accumulation
        const datesInBatch = [...new Set(transactions.map(t => t.date))]
        for (const d of datesInBatch) {
          await deleteSalesForDate(department, d)
        }

        const results = saveSalesBatch(department, transactions)
        sheetSaved += results.length
        totalSaved += results.length

        const allFlags = []
        for (const { record } of results) {
          const flags = generateTransactionFlags(record)
          allFlags.push(...flags)
        }
        if (allFlags.length > 0) {
          saveFlagsBatch(department, allFlags)
          totalFlags += allFlags.length
        }
      }

      if (sheetSaved > 0) {
        importedSheets.push({ name: sheet.sheetName, date: sheet.sheetDate, count: sheetSaved })
      } else if (!skippedSheets.find(s => s.name === sheet.sheetName)) {
        skippedSheets.push({ name: sheet.sheetName, reason: 'No valid data rows found' })
      }
    }

    const sortedDates = [...datesProcessed].sort()
    setImportSummary({ importedSheets, skippedSheets, totalSaved, totalFlags, dates: sortedDates })

    if (totalSaved === 0) {
      addToast('No data was imported. Check the import summary for details.', 'error')
    } else {
      const dayCount = datesProcessed.size
      addToast(
        `Saved ${totalSaved} entries across ${dayCount} day${dayCount !== 1 ? 's' : ''}, ${totalFlags} flags raised`,
        'success'
      )
    }
    setSaved(true)
    onSaved?.()
  }

  const totalStats = parsed ? (() => {
    const allDates = new Set()
    let totalRows = 0
    for (const sheet of parsed) {
      totalRows += sheet.rows.length
      // Use sheetDate if available, otherwise use dates from row data
      if (sheet.sheetDate) {
        allDates.add(sheet.sheetDate)
      } else {
        for (const d of sheet.rowDateRange.dates) allDates.add(d)
      }
    }
    const sorted = [...allDates].sort()
    return { days: sorted.length, totalRows, min: sorted[0], max: sorted[sorted.length - 1] }
  })() : null

  return (
    <div className="space-y-4">
      {!parsed && (
        <>
          <div
            onClick={() => fileRef.current?.click()}
            className="border-2 border-dashed border-gray-700 rounded-lg p-12 text-center cursor-pointer hover:border-gray-500 transition-colors"
          >
            <Upload size={32} className="text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400 text-sm">Drop Excel file here or click to upload</p>
            <p className="text-gray-600 text-xs mt-1">.xlsx format — for importing multiple days of data at once</p>
            <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={handleFile} />
          </div>

          <div className="bg-gray-800/50 rounded-lg p-4 text-xs text-gray-500 space-y-2">
            <p className="text-gray-400 font-medium text-sm">Expected Excel formats:</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {isBakery ? (
                <>
                  <div>
                    <p className="text-gray-400 mb-1">Production / Surplus / Damages</p>
                    <div className="bg-gray-900 rounded p-2 font-mono">
                      <p>Date | Product | Qty</p>
                      <p>2026-01-01 | BRD | 100</p>
                      <p>2026-01-01 | SCN | 200</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-gray-400 mb-1">Sales</p>
                    <div className="bg-gray-900 rounded p-2 font-mono">
                      <p>Date | Product | Qty | Sales</p>
                      <p>2026-01-01 | BRD | 50 | 550</p>
                      <p>2026-01-01 | SCN | 100 | 200</p>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <p className="text-gray-400 mb-1">Morning Log / Surplus</p>
                    <div className="bg-gray-900 rounded p-2 font-mono">
                      <p>Date | Mala | Steak | ...</p>
                      <p>2026-01-01 | 500 | 800 | ...</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-gray-400 mb-1">Sales</p>
                    <div className="bg-gray-900 rounded p-2 font-mono">
                      <p>Date | Product | Qty | Sales</p>
                      <p>2026-01-01 | FMK | 5 | 425</p>
                    </div>
                  </div>
                </>
              )}
              <div>
                <p className="text-gray-400 mb-1">Tips</p>
                <ul className="list-disc pl-4 space-y-1">
                  <li>Multiple sheets supported</li>
                  <li>Sheet names as dates (e.g. "02 Jan 2026") auto-detected</li>
                  <li>Dates can span many days</li>
                  <li>Sheet names help auto-detect type</li>
                </ul>
              </div>
            </div>
          </div>
        </>
      )}

      {loading && (
        <div className="text-center py-8 text-gray-400 text-sm">Parsing Excel...</div>
      )}

      {parsed && !saved && (
        <div className="space-y-4">
          {totalStats && totalStats.days > 0 && (
            <div className="bg-brand-950/30 border border-brand-800 rounded-lg p-4 flex items-center gap-4">
              <CalendarDays size={20} className="text-brand-400" />
              <div>
                <p className="text-sm text-white font-medium">
                  {totalStats.days} day{totalStats.days !== 1 ? 's' : ''} detected
                  <span className="text-gray-400 font-normal ml-2">
                    {totalStats.min === totalStats.max
                      ? totalStats.min
                      : `${totalStats.min} to ${totalStats.max}`
                    }
                  </span>
                </p>
                <p className="text-xs text-gray-500">
                  {totalStats.totalRows} data rows across {parsed.length} sheet{parsed.length !== 1 ? 's' : ''}
                  {parsed.some(s => s.sheetDate) && (
                    <span className="text-brand-400 ml-2">
                      ({parsed.filter(s => s.sheetDate).length} date-named sheet{parsed.filter(s => s.sheetDate).length !== 1 ? 's' : ''})
                    </span>
                  )}
                </p>
              </div>
            </div>
          )}

          {totalStats && totalStats.days <= 1 && (
            <div className="bg-amber-950/30 border border-amber-800 rounded-lg p-3 flex items-center gap-3">
              <Info size={16} className="text-amber-400 shrink-0" />
              <p className="text-xs text-amber-300">
                Only {totalStats.days || 0} day detected. For single-day entries, consider using <strong>Single Day Entry</strong> mode for a faster workflow. You can still proceed with this upload if preferred.
              </p>
            </div>
          )}

          {/* Date override for files without dates — hidden when sheets have dates from names */}
          {!parsed.some(s => s.sheetDate) && (
            <div className="flex items-center gap-3 bg-gray-800/50 rounded-lg px-4 py-3 flex-wrap">
              <span className="text-xs text-gray-400">Override all dates (optional):</span>
              <DateInput value={overrideDate} onChange={setOverrideDate} />
              {overrideDate && (
                <button
                  onClick={() => setOverrideDate('')}
                  className="text-xs text-gray-500 hover:text-white"
                >
                  Clear
                </button>
              )}
              {overrideDate && (
                <span className="text-xs text-amber-400">All rows will use {overrideDate}</span>
              )}
            </div>
          )}

          {/* Info banner when date-named sheets are detected */}
          {parsed.some(s => s.sheetDate) && (
            <div className="bg-brand-950/30 border border-brand-800 rounded-lg p-3 flex items-start gap-3">
              <Info size={16} className="text-brand-400 shrink-0 mt-0.5" />
              <div className="text-xs text-brand-300 space-y-1">
                <p className="font-medium">Date-named sheets detected</p>
                <p className="text-gray-400">
                  Each sheet's name will be used as the date for its data. The date range selector is used for filtering only.
                </p>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {parsed.filter(s => s.sheetDate).map((s, i) => (
                    <span key={i} className="bg-brand-900/50 text-brand-400 px-2 py-0.5 rounded text-[11px]">
                      {s.sheetName} → {s.sheetDate}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center gap-2 text-sm text-gray-400">
            <FileCheck size={16} />
            Found {parsed.length} sheet(s)
          </div>

          {parsed.map((sheet, i) => (
            <div key={i} className="bg-gray-800/50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <span className="font-medium text-sm">{sheet.sheetName}</span>

                {sheet.sheetDate && (
                  <span className="text-xs bg-green-900/50 text-green-400 px-2 py-0.5 rounded">
                    Date: {sheet.sheetDate}
                  </span>
                )}

                <div className="relative">
                  <select
                    value={sheet.type}
                    onChange={e => updateSheetType(i, e.target.value)}
                    className="appearance-none bg-gray-700 text-gray-300 text-xs px-2 py-0.5 rounded pr-5 cursor-pointer hover:bg-gray-600"
                  >
                    {sheetTypeOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <ChevronDown size={10} className="absolute right-1.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                </div>

                <span className="text-xs text-gray-500">{sheet.rows.length} rows</span>

                {!sheet.sheetDate && sheet.rowDateRange.count > 0 && (
                  <span className="text-xs bg-brand-900/50 text-brand-400 px-2 py-0.5 rounded">
                    {sheet.rowDateRange.count} day{sheet.rowDateRange.count !== 1 ? 's' : ''}
                    {sheet.rowDateRange.count > 1 && (
                      <span className="text-brand-500 ml-1">
                        ({sheet.rowDateRange.min} to {sheet.rowDateRange.max})
                      </span>
                    )}
                    {sheet.rowDateRange.count === 1 && (
                      <span className="text-brand-500 ml-1">({sheet.rowDateRange.min})</span>
                    )}
                  </span>
                )}

                {sheet.sheetDate && !sheet.hasDateColumn && (
                  <span className="text-[10px] text-gray-500">(no date column — using sheet name)</span>
                )}
              </div>

              <div className="overflow-x-auto max-h-[50vh] overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-gray-800">
                    <tr className="text-gray-500 border-b border-gray-700">
                      {(sheet.headers || []).slice(0, 8).map((h, j) => (
                        <th key={j} className="text-left py-1 px-2">{String(h)}</th>
                      ))}
                      <th className="py-1 px-2 w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {sheet.rows.map((row, j) => (
                      <tr key={j} className="border-b border-gray-800/50 hover:bg-gray-700/30">
                        {row.slice(0, 8).map((cell, k) => (
                          <td key={k} className="py-0.5 px-1">
                            <input
                              value={formatCellForDisplay(cell)}
                              onChange={e => updateCell(i, j, k, e.target.value)}
                              className="bg-transparent border border-transparent hover:border-gray-600 focus:border-brand-500 rounded px-1 py-0.5 text-xs text-gray-300 w-full outline-none"
                            />
                          </td>
                        ))}
                        <td className="py-0.5 px-1">
                          <button
                            onClick={() => removeRow(i, j)}
                            className="text-gray-600 hover:text-red-400"
                          >
                            <Trash2 size={12} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}

          {unmatched.length > 0 && (
            <div className="bg-amber-950/30 border border-amber-800 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3 text-amber-400 text-sm">
                <AlertCircle size={16} />
                {unmatched.length} unmatched product(s) will be skipped unless added
              </div>
              <div className="space-y-2">
                {unmatched.map((u, i) => (
                  <div key={i} className="flex items-center justify-between bg-gray-800/50 rounded px-3 py-2">
                    <span className="text-sm text-gray-300">{u.rawName}</span>
                    <button
                      onClick={() => { setAddProductForUnmatched(i); setShowAddProduct(true) }}
                      className="text-xs bg-brand-700 hover:bg-brand-600 text-white px-3 py-1 rounded"
                    >
                      + Create Product
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <button
            onClick={saveAll}
            className="flex items-center gap-2 bg-brand-600 hover:bg-brand-500 text-white px-6 py-2.5 rounded-lg text-sm font-medium"
          >
            <Check size={16} /> Confirm & Save All
          </button>
        </div>
      )}

      {saved && (
        <div className="space-y-4">
          <div className="text-center py-4">
            <Check size={32} className="text-green-400 mx-auto mb-2" />
            <p className="text-green-400 text-sm">Data saved successfully</p>
          </div>

          {/* Import Summary */}
          {importSummary && (
            <div className="bg-gray-800/50 rounded-lg p-4 space-y-3">
              <p className="text-sm font-medium text-white">Import Summary</p>

              {importSummary.importedSheets.length > 0 && (
                <div className="space-y-1">
                  <p className="text-xs text-green-400 font-medium">
                    Successfully imported {importSummary.importedSheets.length} sheet{importSummary.importedSheets.length !== 1 ? 's' : ''}
                    {importSummary.dates.length > 0 && ` across ${importSummary.dates.length} day${importSummary.dates.length !== 1 ? 's' : ''}`}:
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {importSummary.importedSheets.map((s, i) => (
                      <span key={i} className="text-[11px] bg-green-900/40 text-green-300 px-2 py-0.5 rounded">
                        {s.name}{s.date ? ` (${s.date})` : ''} — {s.count} entries
                      </span>
                    ))}
                  </div>
                  {importSummary.dates.length > 0 && (
                    <p className="text-[11px] text-gray-500 mt-1">
                      Dates: {importSummary.dates.join(', ')}
                    </p>
                  )}
                </div>
              )}

              {importSummary.skippedSheets.length > 0 && (
                <div className="space-y-1">
                  <p className="text-xs text-amber-400 font-medium">
                    Skipped {importSummary.skippedSheets.length} sheet{importSummary.skippedSheets.length !== 1 ? 's' : ''}:
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {importSummary.skippedSheets.map((s, i) => (
                      <span key={i} className="text-[11px] bg-amber-900/40 text-amber-300 px-2 py-0.5 rounded">
                        {s.name} — {s.reason}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {importSummary.totalFlags > 0 && (
                <p className="text-xs text-gray-400">{importSummary.totalFlags} flags raised</p>
              )}
            </div>
          )}

          <div className="text-center">
            <button
              onClick={() => { setParsed(null); setSaved(false); setImportSummary(null) }}
              className="text-gray-400 text-xs hover:text-white"
            >
              Upload another file
            </button>
          </div>
        </div>
      )}

      {showAddProduct && (
        <AddProductModal
          department={department}
          prefillName={addProductForUnmatched !== null ? unmatched[addProductForUnmatched]?.rawName : ''}
          onClose={() => { setShowAddProduct(false); setAddProductForUnmatched(null) }}
          onProductAdded={() => {
            if (addProductForUnmatched !== null) {
              // Trigger re-parse by forcing parsed state update
              setParsed(prev => prev ? [...prev] : prev)
            }
          }}
        />
      )}
    </div>
  )
}
